
import React, { useMemo, useState } from 'react';
import { Reservation, Table, Zone, CategoryDefinition } from '../types';
import { History, Calendar, Clock, Users, MapPin, Target, MessageSquare, Mail, Phone, Tag, Search, Filter, X, LayoutGrid, ChevronLeft, ChevronRight, CalendarRange, Timer, ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';

interface AdminReservationHistoryProps {
  reservations: Reservation[];
  tables: Table[];
  zones: Zone[];
  categories: CategoryDefinition[];
}

const ITEMS_PER_PAGE = 50;

export const AdminReservationHistory: React.FC<AdminReservationHistoryProps> = ({
  reservations,
  tables,
  zones,
  categories
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterZoneId, setFilterZoneId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Combined real data and dummy data (Expanded to 70 entries)
  const rawHistoryData = useMemo(() => {
    const now = new Date();
    
    // 1. Real confirmed reservations that are in the past
    const realHistory = reservations.filter(r => {
        const resDate = new Date(`${r.date}T${r.time}`);
        return resDate < now && r.status === 'CONFIRMED';
    });

    // 2. Generate 70 dummy entries
    const dummyData: Reservation[] = [];
    const firstNames = ['Markus', 'Sarah', 'Thomas', 'Elena', 'Christian', 'Julia', 'Andreas', 'Melanie'];
    const lastNames = ['Müller', 'Schmidt', 'Schneider', 'Fischer', 'Weber', 'Meyer', 'Wagner', 'Becker'];
    const sports = tables.length > 0 ? tables.map(t => t.id) : ['t1', 't2', 't3', 't4', 't5', 't6'];

    for (let i = 1; i <= 70; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (i % 60 + 1)); // Spread over last 60 days
      const dateStr = date.toISOString().split('T')[0];
      
      const fName = firstNames[i % firstNames.length];
      const lName = lastNames[i % lastNames.length];
      const tableId = sports[i % sports.length];
      // Fix: find table to get categoryId
      const table = tables.find(t => t.id === tableId);

      dummyData.push({
        id: `dummy-hist-${i}`,
        tableId: tableId,
        categoryId: table?.category || 'SOCCER_LARGE', // Fix: Added categoryId
        customerName: i % 5 === 0 ? `Sportverein ${lName} e.V.` : `${fName} ${lName}`,
        date: dateStr,
        time: `${10 + (i % 10)}:00`,
        durationMinutes: 60 + (i % 4) * 15,
        partySize: 2 + (i % 10),
        notes: i % 7 === 0 ? 'Leihschläger benötigt' : '',
        email: `${fName.toLowerCase()}.${lName.toLowerCase()}@example.com`,
        phone: `0151-${1000000 + i}`,
        status: 'CONFIRMED'
      });
    }

    return [...realHistory, ...dummyData];
  }, [reservations, tables]);

  const getTableRect = (table: Table) => {
    const width = table.width || 60;
    const height = table.height || 60;
    return { x: table.x, y: table.y, width, height };
  };

  const getIntersectionArea = (r1: {x:number, y:number, width:number, height:number}, r2: Zone) => {
    const xOverlap = Math.max(0, Math.min(r1.x + r1.width, r2.x + r2.width) - Math.max(r1.x, r2.x));
    const yOverlap = Math.max(0, Math.min(r1.y + r1.height, r2.y + r2.height) - Math.max(r1.y, r2.y));
    return xOverlap * yOverlap;
  };

  const getZoneForTable = (table: Table) => {
    const tableRect = getTableRect(table);
    let maxArea = 0;
    let foundZone: Zone | null = null;

    zones.forEach(zone => {
      const area = getIntersectionArea(tableRect, zone);
      if (area > maxArea) {
        maxArea = area;
        foundZone = zone;
      }
    });
    return foundZone;
  };

  // Filtered and Searchable History Data
  const filteredHistory = useMemo(() => {
    const filtered = rawHistoryData.filter(res => {
      const table = tables.find(t => t.id === res.tableId);
      const zone = table ? getZoneForTable(table) : null;
      
      const searchTerms = searchQuery.toLowerCase().split(' ');
      const contentString = `
        ${res.customerName} 
        ${res.email || ''} 
        ${res.phone || ''} 
        ${res.notes || ''} 
        ${table?.name || ''} 
        ${(zone as any)?.name || ''} 
        ${res.date}
      `.toLowerCase();

      const matchesSearch = searchTerms.every(term => contentString.includes(term));
      const matchesCategory = !filterCategoryId || (table && table.category === filterCategoryId);
      const matchesZone = !filterZoneId || (zone && (zone as any).id === filterZoneId);

      const matchesDateFrom = !dateFrom || res.date >= dateFrom;
      const matchesDateTo = !dateTo || res.date <= dateTo;

      const matchesTimeFrom = !timeFrom || res.time >= timeFrom;
      const matchesTimeTo = !timeTo || res.time <= timeTo;

      return matchesSearch && matchesCategory && matchesZone && matchesDateFrom && matchesDateTo && matchesTimeFrom && matchesTimeTo;
    }).sort((a, b) => new Date(`${b.date}T${b.time}`).getTime() - new Date(`${a.date}T${a.time}`).getTime());
    
    return filtered;
  }, [rawHistoryData, searchQuery, filterCategoryId, filterZoneId, dateFrom, dateTo, timeFrom, timeTo, tables, zones]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredHistory.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredHistory, currentPage]);

  const resetFilters = () => {
    setSearchQuery('');
    setFilterCategoryId('');
    setFilterZoneId('');
    setDateFrom('');
    setDateTo('');
    setTimeFrom('');
    setTimeTo('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeFiltersCount = [
    searchQuery, filterCategoryId, filterZoneId, dateFrom, dateTo, timeFrom, timeTo
  ].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 animate-fade-in">
      
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
           <h2 className="text-xl md:text-2xl font-black text-slate-800 flex items-center gap-2 uppercase italic tracking-tighter">
             <History className="theme-text-primary" /> Buchungshistorie
           </h2>
           <p className="text-sm text-slate-500 font-medium">Archiv aller abgeschlossenen Spiele & Trainingseinheiten.</p>
        </div>
        <div className="flex items-center gap-3">
            <div className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-4 py-2 rounded-xl shadow-sm text-sm font-black uppercase tracking-tighter">
                {filteredHistory.length} Treffer
            </div>
            {activeFiltersCount > 0 && (
              <button 
                onClick={resetFilters}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-slate-100 bg-white shadow-sm flex items-center gap-2"
                title="Filter zurücksetzen"
              >
                <X size={18} />
                <span className="text-[10px] font-black uppercase pr-1">Reset</span>
              </button>
            )}
        </div>
      </div>

      {/* Collapsible Filter Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden transition-all duration-300">
          <button 
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-transparent group"
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg transition-colors ${isFiltersExpanded ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-emerald-100 group-hover:text-emerald-600'}`}>
                <SlidersHorizontal size={18} />
              </div>
              <span className="font-black text-xs uppercase tracking-widest text-slate-700">Filter & Suche</span>
              {activeFiltersCount > 0 && (
                <span className="bg-emerald-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-black ml-1 animate-pulse">
                  {activeFiltersCount}
                </span>
              )}
            </div>
            {isFiltersExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
          </button>

          <div className={`transition-all duration-300 ease-in-out ${isFiltersExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
            <div className="p-6 pt-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="relative md:col-span-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Globale Suche</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 text-slate-400" size={18} />
                      <input 
                        type="text" 
                        placeholder="Gast, Email, Platz, Notiz..."
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm font-medium"
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Sportart</label>
                    <div className="relative">
                      <LayoutGrid className="absolute left-3 top-3 text-slate-400" size={18} />
                      <select 
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm font-bold appearance-none cursor-pointer"
                        value={filterCategoryId}
                        onChange={e => { setFilterCategoryId(e.target.value); setCurrentPage(1); }}
                      >
                        <option value="">Alle Sportarten</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="relative">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Bereich/Halle</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 text-slate-400" size={18} />
                      <select 
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm font-bold appearance-none cursor-pointer"
                        value={filterZoneId}
                        onChange={e => { setFilterZoneId(e.target.value); setCurrentPage(1); }}
                      >
                        <option value="">Alle Hallen/Bereiche</option>
                        {zones.filter(z => (z.type as any) === 'DINING' || z.type === 'CUSTOM').map(zone => (
                          <option key={zone.id} value={zone.id}>{zone.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-slate-50">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Datum von</label>
                    <div className="relative">
                        <CalendarRange className="absolute left-3 top-3 text-slate-400" size={16} />
                        <input 
                            type="date" 
                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-emerald-500 transition-all cursor-pointer"
                            value={dateFrom}
                            onChange={e => { setDateFrom(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Datum bis</label>
                    <div className="relative">
                        <CalendarRange className="absolute left-3 top-3 text-slate-400" size={16} />
                        <input 
                            type="date" 
                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-emerald-500 transition-all cursor-pointer"
                            value={dateTo}
                            onChange={e => { setDateTo(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Uhrzeit ab (24h)</label>
                    <div className="relative">
                        <Timer className="absolute left-3 top-3 text-slate-400" size={16} />
                        <input 
                            type="time" 
                            step="900"
                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-emerald-500 transition-all cursor-pointer"
                            value={timeFrom}
                            onChange={e => { setTimeFrom(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Uhrzeit bis (24h)</label>
                    <div className="relative">
                        <Timer className="absolute left-3 top-3 text-slate-400" size={16} />
                        <input 
                            type="time" 
                            step="900"
                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-emerald-500 transition-all cursor-pointer"
                            value={timeTo}
                            onChange={e => { setTimeTo(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                  </div>
              </div>
            </div>
          </div>
      </div>

      {/* History Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-[10px] text-slate-400 uppercase tracking-widest bg-slate-50/50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 font-bold">Datum & Zeit</th>
                <th className="px-6 py-4 font-bold">Gast / Verein</th>
                <th className="px-6 py-4 font-bold">Halle / Platz</th>
                <th className="px-6 py-4 font-bold text-center">Team</th>
                <th className="px-6 py-4 font-bold">Dauer</th>
                <th className="px-6 py-4 font-bold">Kontakt</th>
                <th className="px-6 py-4 font-bold">Notiz</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {currentItems.map((res) => {
                const table = tables.find(t => t.id === res.tableId);
                const zone = table ? getZoneForTable(table) : null;
                const dateObj = new Date(res.date);
                
                return (
                  <tr key={res.id} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-6 py-4 whitespace-nowrap">
                       <div className="flex items-center gap-3">
                         <div className="w-8 h-8 bg-slate-100 text-slate-500 rounded-lg flex items-center justify-center group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors">
                            <Calendar size={14} />
                         </div>
                         <div>
                            <div className="font-bold text-slate-900">{dateObj.toLocaleDateString('de-DE')}</div>
                            <div className="text-[10px] text-slate-400 font-bold flex items-center gap-1">
                                <Clock size={10} /> {res.time} Uhr
                            </div>
                         </div>
                       </div>
                    </td>
                    <td className="px-6 py-4">
                        <div className="font-bold text-slate-800">{res.customerName}</div>
                        <div className="text-[10px] text-slate-400 flex items-center gap-1">
                            <Tag size={10} /> ID: {res.id.slice(0, 8)}
                        </div>
                    </td>
                    <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded w-fit">
                                <MapPin size={10} /> {zone ? (zone as any).name : 'Allgemein'}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded w-fit">
                                <Target size={10} /> {table ? table.name : 'Unbekannt'}
                            </span>
                        </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                        <div className="inline-flex items-center gap-1.5 font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-lg">
                            <Users size={12} /> {res.partySize}
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-600">
                        {res.durationMinutes} Min.
                    </td>
                    <td className="px-6 py-4">
                        <div className="flex flex-col gap-0.5 max-w-[180px]">
                            {res.email && (
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 truncate" title={res.email}>
                                    <Mail size={10} className="shrink-0" /> {res.email}
                                </div>
                            )}
                            {res.phone && (
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                                    <Phone size={10} className="shrink-0" /> {res.phone}
                                </div>
                            )}
                            {!res.email && !res.phone && <span className="text-[10px] italic text-slate-300">Keine Daten</span>}
                        </div>
                    </td>
                    <td className="px-6 py-4">
                        {res.notes ? (
                            <div className="text-[10px] text-slate-500 italic bg-amber-50 p-2 rounded border border-amber-100/50 flex items-start gap-1.5 max-w-[150px]">
                                <MessageSquare size={10} className="mt-0.5 shrink-0 opacity-50" />
                                <span className="line-clamp-2">{res.notes}</span>
                            </div>
                        ) : (
                            <span className="text-[10px] text-slate-200">-</span>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredHistory.length === 0 ? (
            <div className="text-center py-24 bg-white">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search size={32} className="text-slate-200" />
                </div>
                <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">Keine Ergebnisse für deine Filter</p>
                <button onClick={resetFilters} className="mt-4 text-emerald-600 font-bold text-sm hover:underline">Filter zurücksetzen</button>
            </div>
        ) : (
          /* Pagination Controls */
          <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
              <div className="text-xs text-slate-500 font-medium">
                  Zeige <span className="font-bold text-slate-900">{Math.min(filteredHistory.length, (currentPage - 1) * ITEMS_PER_PAGE + 1)}</span> bis <span className="font-bold text-slate-900">{Math.min(filteredHistory.length, currentPage * ITEMS_PER_PAGE)}</span> von <span className="font-bold text-slate-900">{filteredHistory.length}</span> Einträgen
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  
                  <div className="flex items-center gap-1 mx-2">
                    {[...Array(totalPages)].map((_, idx) => {
                      const pageNum = idx + 1;
                      if (totalPages > 7) {
                        if (pageNum > 1 && pageNum < totalPages && (pageNum < currentPage - 1 || pageNum > currentPage + 1)) {
                          if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                            return <span key={`dots-${pageNum}`} className="px-1 text-slate-400">...</span>;
                          }
                          return null;
                        }
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-xs font-black transition-all ${
                            currentPage === pageNum 
                            ? 'bg-slate-900 text-white shadow-md' 
                            : 'bg-white text-slate-600 border border-slate-200 hover:border-emerald-500 hover:text-emerald-600'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};
